import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

/**
 * Notification center — the in-app Pulse / Alert / Notification feed.
 *
 * Backed by /api/notification-center* (always scoped server-side to the authed
 * user). Distinct from the legacy /users/{id}/notifications badge counters and
 * from the bell's bespoke cost-change queue — this is the general feed.
 */

export const CATEGORY_LABELS = {
  pulse: "Pulse",
  alert: "Alert",
  notification: "Notification",
};

/** Badge tone per category — pulse sells, alert warns, the rest stays quiet. */
export const CATEGORY_TONES = {
  pulse: "purple",
  alert: "danger",
  notification: "muted",
};

/**
 * The bell's payload: `{ unread_count, latest[] }`. Ambient — polls so a new
 * lead surfaces without a reload. Same cadence as the legacy UserContext
 * notification poll (~30s) so the two never disagree for long.
 */
export const useNotificationSummary = (enabled = true) =>
  useQuery({
    queryKey: ["notification-center", "summary"],
    enabled,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await axios.get("/api/notification-center/summary", {
        metadata: { silent: true },
      });
      return data ?? { unread_count: 0, latest: [] };
    },
  });

/** The /notifications page — infinite list with category / unread filters. */
export const useNotificationList = (filters = {}) =>
  useInfiniteQuery({
    queryKey: ["notification-center", "list", filters],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await axios.get("/api/notification-center", {
        params: {
          page: pageParam,
          per_page: 25,
          ...(filters.category ? { category: filters.category } : {}),
          ...(filters.unreadOnly ? { is_read: 0 } : {}),
        },
        metadata: { silent: true },
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (!meta || meta.current_page >= meta.last_page) return undefined;
      return meta.current_page + 1;
    },
  });

export const markNotificationRead = async (id) => {
  const { data } = await axios.put(`/api/notification-center/${id}/read`, null, {
    metadata: { silent: true },
  });
  return data;
};

export const markAllNotificationsRead = async () => {
  const { data } = await axios.put("/api/notification-center/read-all", null, {
    metadata: { silent: true },
  });
  return data;
};

/** Refresh the bell and every open list after a read/read-all. */
export const useRefreshNotifications = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["notification-center"] });
};
